import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { ContextSession } from "../../services/ContextSession";
import {
  actualizarFirmaMantenimiento,
  crearFirmaMantenimiento,
  listarFirmasMantenimiento,
} from "./enrolamientoApi";

const EMPTY_FORM = {
  nombreCompleto: "",
  nombrePuesto: "",
  observacion: "",
  estado: true,
};

const EMPTY_FILTERS = {
  id: "",
  nombreCompleto: "",
  nombrePuesto: "",
  estado: "TODOS",
  tieneImagen: "TODOS",
};

const EMPTY_PAGINATION = {
  page: 1,
  page_size: 15,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

export default function FirmaEnrolamiento() {
  const session = useContext(ContextSession);
  const authToken = session?.getToken ? session.getToken() : "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [incluirInactivos, setIncluirInactivos] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const totalActivos = useMemo(
    () => items.filter((item) => Boolean(item?.Estado)).length,
    [items],
  );

  const filteredItems = useMemo(() => {
    const idFilter = String(filters.id || "").trim();
    const nombreFilter = String(filters.nombreCompleto || "").trim().toUpperCase();
    const puestoFilter = String(filters.nombrePuesto || "").trim().toUpperCase();
    const estadoFilter = String(filters.estado || "TODOS").toUpperCase();
    const imagenFilter = String(filters.tieneImagen || "TODOS").toUpperCase();

    return items.filter((row) => {
      const rowId = String(row?.FirmaId ?? "");
      const rowNombre = String(row?.NombreCompleto || "").toUpperCase();
      const rowPuesto = String(row?.NombrePuesto || "").toUpperCase();
      const rowEstado = row?.Estado ? "ACTIVO" : "INACTIVO";
      const rowImagen = row?.TieneImagen ? "SI" : "NO";

      if (idFilter && !rowId.includes(idFilter)) return false;
      if (nombreFilter && !rowNombre.includes(nombreFilter)) return false;
      if (puestoFilter && !rowPuesto.includes(puestoFilter)) return false;
      if (estadoFilter !== "TODOS" && rowEstado !== estadoFilter) return false;
      if (imagenFilter !== "TODOS" && rowImagen !== imagenFilter) return false;
      return true;
    });
  }, [items, filters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listarFirmasMantenimiento(authToken, {
        incluirInactivos,
        incluirImagen: false,
        page: page + 1,
        pageSize: rowsPerPage,
      });
      const rows = Array.isArray(result?.items) ? result.items : [];
      const paginationData = result?.pagination || EMPTY_PAGINATION;
      setItems(rows);
      setPagination(paginationData);
      const serverPage = Math.max((Number(paginationData?.page) || 1) - 1, 0);
      if (serverPage !== page) {
        setPage(serverPage);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el mantenimiento de firmas.");
    } finally {
      setLoading(false);
    }
  }, [authToken, incluirInactivos, page, rowsPerPage]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setSelectedFile(null);
    setPreviewSrc("");
  };

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (!file) {
      setPreviewSrc("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewSrc(String(reader.result || ""));
    };
    reader.readAsDataURL(file);
  };

  const handleEdit = async (row) => {
    const id = row?.FirmaId;
    if (id === undefined || id === null || id === "") return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const detail = await listarFirmasMantenimiento(authToken, {
        firmaId: id,
        incluirImagen: true,
        incluirInactivos: true,
      });
      setEditingId(Number(detail?.FirmaId || id));
      setForm({
        nombreCompleto: String(detail?.NombreCompleto || ""),
        nombrePuesto: String(detail?.NombrePuesto || ""),
        observacion: String(detail?.Observacion || ""),
        estado: Boolean(detail?.Estado),
      });
      setSelectedFile(null);
      setPreviewSrc(String(detail?.ImagenDataUrl || ""));
      setModalOpen(true);
    } catch (err) {
      setError(err.message || "No se pudo cargar la firma para editar.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const nombreCompleto = String(form.nombreCompleto || "").trim().toUpperCase();
    const nombrePuesto = String(form.nombrePuesto || "").trim().toUpperCase();
    const observacion = String(form.observacion || "").trim();

    if (!nombreCompleto) {
      setError("Debe ingresar nombre completo.");
      return;
    }
    if (!nombrePuesto) {
      setError("Debe ingresar nombre del puesto.");
      return;
    }

    const payload = {
      NombreCompleto: nombreCompleto,
      NombrePuesto: nombrePuesto,
      Observacion: observacion,
      Estado: Boolean(form.estado),
    };

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      if (editingId !== null && editingId !== undefined) {
        await actualizarFirmaMantenimiento(authToken, editingId, payload, {
          imagenFile: selectedFile || undefined,
        });
        setSuccess("Firma actualizada correctamente.");
      } else {
        await crearFirmaMantenimiento(authToken, payload, {
          imagenFile: selectedFile || undefined,
        });
        setSuccess("Firma creada correctamente.");
      }
      resetForm();
      setModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || "No se pudo guardar la firma.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c" }}>
              Firma - Mantenimiento
            </Typography>
            <Typography sx={{ color: "#5a6a80", fontSize: 14 }}>
              Registros: {pagination.total} | Activos en página: {totalActivos}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={incluirInactivos}
                  onChange={(event) => {
                    setIncluirInactivos(event.target.checked);
                    setPage(0);
                  }}
                />
              }
              label="Mostrar inactivos"
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || saving}
            >
              Recargar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
              sx={{ backgroundColor: "#19498c" }}
            >
              Nuevo
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Filtro ID"
              value={filters.id}
              onChange={(event) => setFilters((prev) => ({ ...prev, id: event.target.value }))}
              sx={{ width: { xs: "100%", md: 140 } }}
            />
            <TextField
              label="Filtro nombre completo"
              value={filters.nombreCompleto}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, nombreCompleto: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Filtro puesto"
              value={filters.nombrePuesto}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, nombrePuesto: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Filtro estado"
              select
              value={filters.estado}
              onChange={(event) => setFilters((prev) => ({ ...prev, estado: event.target.value }))}
              sx={{ width: { xs: "100%", md: 180 } }}
            >
              <MenuItem value="TODOS">TODOS</MenuItem>
              <MenuItem value="ACTIVO">ACTIVO</MenuItem>
              <MenuItem value="INACTIVO">INACTIVO</MenuItem>
            </TextField>
            <TextField
              label="Filtro imagen"
              select
              value={filters.tieneImagen}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, tieneImagen: event.target.value }))
              }
              sx={{ width: { xs: "100%", md: 180 } }}
            >
              <MenuItem value="TODOS">TODOS</MenuItem>
              <MenuItem value="SI">SI</MenuItem>
              <MenuItem value="NO">NO</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => setFilters(EMPTY_FILTERS)}>
              Limpiar filtros
            </Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#19498c" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Nombre completo</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Puesto</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Imagen</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: 120 }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Sin registros.
                  </TableCell>
                </TableRow>
              )}
              {filteredItems.map((row) => (
                <TableRow key={row.FirmaId}>
                  <TableCell>{row.FirmaId}</TableCell>
                  <TableCell>{row.NombreCompleto}</TableCell>
                  <TableCell>{row.NombrePuesto}</TableCell>
                  <TableCell>{row.Estado ? "ACTIVO" : "INACTIVO"}</TableCell>
                  <TableCell>{row.TieneImagen ? "SI" : "NO"}</TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => handleEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={Number(pagination?.total || 0)}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const nextRowsPerPage = parseInt(event.target.value, 10) || 15;
              setRowsPerPage(nextRowsPerPage);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
          />
        </Paper>
      </Paper>

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>{editingId !== null ? "Editar Firma" : "Nueva Firma"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Nombre completo"
                value={form.nombreCompleto}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    nombreCompleto: event.target.value.toUpperCase(),
                  }))
                }
                fullWidth
              />
              <TextField
                label="Nombre puesto"
                value={form.nombrePuesto}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    nombrePuesto: event.target.value.toUpperCase(),
                  }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Observación"
                value={form.observacion}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    observacion: event.target.value,
                  }))
                }
                fullWidth
              />
              <FormControlLabel
                label={form.estado ? "ACTIVO" : "INACTIVO"}
                control={
                  <Switch
                    checked={Boolean(form.estado)}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, estado: event.target.checked }))
                    }
                  />
                }
              />
              <Button variant="outlined" component="label">
                Seleccionar firma
                <input hidden accept="image/*" type="file" onChange={handleFileChange} />
              </Button>
            </Stack>

            {previewSrc && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#19498c", mb: 1 }}>
                  Vista previa de firma
                </Typography>
                <Box
                  component="img"
                  src={previewSrc}
                  alt="Firma"
                  sx={{
                    maxHeight: 120,
                    maxWidth: "100%",
                    border: "1px solid #dce7f7",
                    borderRadius: 1.5,
                    p: 1,
                    bgcolor: "#fff",
                  }}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ backgroundColor: "#19498c" }}
          >
            {editingId !== null ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
